/**
 * 🔐 Helpers para verificar permissões de usuário
 * 
 * Centraliza a lógica de permissões para evitar duplicação e inconsistências.
 * Alinhado com a lógica do backend (ListTicketsService.ts, CountTicketsByStatusService.ts)
 */

/**
 * Verifica se o usuário é admin ou super (acesso total, ignora permissões)
 * Usado para verificações de menu e permissões configuráveis
 * @param {Object} user - Objeto do usuário
 * @returns {boolean}
 */
export const isFullAdmin = (user) => {
  if (!user?.profile) return false;
  const profile = user.profile.toLowerCase();
  // Apenas admin e super têm acesso total (ignora permissões configuradas)
  return ["admin", "super", "superadmin"].includes(profile);
};

/**
 * Verifica se o usuário é admin, super ou supervisor
 * Usado para funcionalidades que supervisores também podem acessar
 * @param {Object} user - Objeto do usuário
 * @returns {boolean}
 */
export const isAdminProfile = (user) => {
  if (!user?.profile) return false;
  const profile = user.profile.toLowerCase();
  // Incluir perfis administrativos: admin, super, superadmin, supervisor
  return ["admin", "super", "superadmin", "supervisor"].includes(profile);
};

/**
 * Verifica se o usuário tem permissão allHistoric
 * @param {Object} user - Objeto do usuário
 * @returns {boolean}
 */
export const hasAllHistoricPermission = (user) => {
  return user?.allHistoric?.toLowerCase() === "enabled";
};

/**
 * Verifica se o usuário tem permissão allUserChat
 * @param {Object} user - Objeto do usuário
 * @returns {boolean}
 */
export const hasAllUserChatPermission = (user) => {
  return user?.allUserChat?.toLowerCase() === "enabled";
};

/**
 * Verifica se o usuário tem permissão allTicket (ver tickets sem fila)
 * @param {Object} user - Objeto do usuário
 * @returns {boolean}
 */
export const hasAllTicketPermission = (user) => {
  return user?.allTicket?.toLowerCase() === "enabled";
};

/**
 * Verifica se o usuário pode ver TODOS os tickets (independente de fila)
 * Equivalente à lógica do backend para showAll=true
 * 
 * @param {Object} user - Objeto do usuário
 * @returns {boolean}
 */
export const canSeeAllTickets = (user) => {
  return (
    isAdminProfile(user) ||
    hasAllHistoricPermission(user) ||
    hasAllUserChatPermission(user)
  );
};

/**
 * Verifica se o usuário pode ver grupos
 * @param {Object} user - Objeto do usuário
 * @returns {boolean}
 */
export const canAccessGroups = (user) => {
  return user?.allowGroup === true;
};

const userPermissions = {
  isFullAdmin,
  isAdminProfile,
  hasAllHistoricPermission,
  hasAllUserChatPermission,
  hasAllTicketPermission,
  canSeeAllTickets,
  canAccessGroups,
};

export default userPermissions;

